// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';

import {
  spacing,
  fontSize,
  fontWeight,
  borderRadius,
} from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Select from 'presentations/views/components/atoms/Select';
import ChipTextField from 'presentations/views/components/atoms/ChipTextField';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import { black, blueGrayPale, primary } from 'presentations/views/styles/color';
import { helpPath, pricingPath } from 'presentations/routes/path';
import { Role } from 'domains/models/team_user/role';
import { ViewerItem } from 'presentations/api/schema/user';
import { PlanId } from 'domains/models/subscription/planId';
import { useUserInviteForm } from './hooks';
import { AdminTeamUserListItem } from 'presentations/api/schema/team_user';
import TextField from '../../atoms/TextField';
import { getDateTime } from 'infras/datetime';

interface Props {
  viewer: ViewerItem;
  onInvited?: (newUsers: AdminTeamUserListItem[]) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = useUserInviteForm({
    viewer: props.viewer,
    onInvited: props.onInvited,
  });

  const roleOptions: any = [];
  const isPaid = props.viewer.planId > PlanId.Free;
  const isTeam = props.viewer.planId >= PlanId.Team;
  const isEp = props.viewer.planId >= PlanId.Enterprice;

  if (props.viewer.role <= Role.Admin) {
    roleOptions.push({ value: Role.Admin, text: t('Admin') });
    roleOptions.push({ value: Role.Member, text: t('Member') });
    roleOptions.push({ value: Role.Restricted, text: t('Restricted') });
  }

  return (
    <StyledDialog
      isOpen={state.isOpen}
      onClose={actions.onCancel}
      title={t('title')}
      isFillWidth
      actions={
        <DialogSubmitField
          isLoading={state.isLoading}
          onSubmit={actions.onSubmit}
          onCancel={actions.onCancel}
          isDisabled={
            state.emails.length === 0 ||
            Object.keys(state.errors).length > 0 ||
            !state.canAddQuantity
          }
          submitLabel={t('Submit')}
        />
      }
    >
      <Block>{t('description')}</Block>
      {isPaid && state.isTrialEnded && !isEp && (
        <BillingBlock>
          <SeatCount>
            {t('Seats count')} {state.totalTeamUserCount} / {state.quantity}
          </SeatCount>
          <PageLinkContainer>
            <LinkIcon name="chevron-right" />
            <PageLink onClick={actions.onClickEditPlan}>
              {t('Change seats')}
            </PageLink>
          </PageLinkContainer>
        </BillingBlock>
      )}
      {!state.canAddQuantity && (
        <Block>
          <InfoIcon name="alert-circle-outline" />
          {t('Upgrade Message')}
        </Block>
      )}
      <Block>
        {state.canAddQuantity ? (
          <ChipTextField
            isNotRequired
            isFullWidth
            name={'email'}
            value={state.emails}
            inputValue={state.inputingEmail}
            onInputChange={actions.onInputChangeEmail}
            placeholder={state.emails.length === 0 ? t('placeholder') : ''}
            onDeleteTag={actions.onDeleteEmail}
            onSubmit={actions.onAddEmail}
            errors={state.errors}
            errorTagIndexList={state.errorEmailIndexList}
            onPaste={actions.onPaste}
            isDisabled={!state.canAddQuantity}
          />
        ) : (
          <TextField
            name={`email`}
            value={''}
            placeholder={t('placeholder')}
            onChange={null}
            isFullWidth
            isDisabled={true}
          />
        )}
        {isTeam && (
          <SelectWrap
            name={`role`}
            value={state.inputingRole}
            onChange={actions.onInputChangeRole}
            isFullWidth
            isDisabled={!state.canAddQuantity}
            options={roleOptions}
          />
        )}
      </Block>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 400px;
  }
`;
const Block = styled.div`
  margin-bottom: ${spacing(24)};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const SelectWrap = styled(Select)`
  width: 100%;
  margin: ${spacing(8)} 0;
  .MuiSelect-select {
    font-size: ${fontSize(14)};
  }
`;

const InfoIcon = styled(Icon)`
  width: 16px !important;
  height: 16px !important;
  vertical-align: text-bottom;
  color: ${primary};
`;
const BillingBlock = styled(Block)`
  display: flex;
  justify-content: space-between;
  padding: ${spacing(12)} ${spacing(16)};
  background-color: ${blueGrayPale};
  border-radius: ${borderRadius(12)};
  font-size: ${fontSize(14)};
  text-align: center;
  align-items: center;
`;
const PageLinkContainer = styled.div`
  display: flex;
  align-items: center;
`;
const LinkIcon = styled(Icon)`
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(4)};
`;
const PageLink = styled(Link)`
  color: ${black};
`;
const SeatCount = styled.div`
  font-weight: ${fontWeight('bold')};
`;
